import { useState } from 'react'
import { Button } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { Label } from '@creanizer/ui'
import { Textarea } from '@creanizer/ui'
import { Checkbox } from '@creanizer/ui'
import { Alert, AlertDescription } from '@creanizer/ui'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@creanizer/ui'

interface SunoLyricsRegenerateDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  targetSections: string[]
  onRegenerate: (settings: {
    forbiddenWords: string[]
    regenerateReason?: string
    preserveOtherSections: boolean
  }) => void
  isGenerating?: boolean
  error?: string | null
}

export function SunoLyricsRegenerateDialog({
  open,
  onOpenChange,
  targetSections,
  onRegenerate,
  isGenerating = false,
  error = null,
}: SunoLyricsRegenerateDialogProps) {
  const [forbiddenWordsText, setForbiddenWordsText] = useState('')
  const [regenerateReason, setRegenerateReason] = useState('')
  const [preserveOtherSections, setPreserveOtherSections] = useState(true)

  const handleSubmit = () => {
    const forbiddenWords = forbiddenWordsText
      .split(',')
      .map(w => w.trim())
      .filter(Boolean)

    onRegenerate({
      forbiddenWords,
      regenerateReason: regenerateReason || undefined,
      preserveOtherSections,
    })

    // リセット
    setForbiddenWordsText('')
    setRegenerateReason('')
    setPreserveOtherSections(true)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>歌詞を再生成</DialogTitle>
          <DialogDescription>
            {targetSections.length === 1
              ? `「${targetSections[0]}」を再生成します`
              : `${targetSections.length}個のセクションを再生成します`}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-4 py-4">
          {/* 禁止ワード */}
          <div className="space-y-2">
            <Label htmlFor="forbidden-words">禁止ワード（カンマ区切り）</Label>
            <Input
              id="forbidden-words"
              placeholder="例: love, heart, dream"
              value={forbiddenWordsText}
              onChange={(e) => setForbiddenWordsText(e.target.value)}
              disabled={isGenerating}
            />
            <p className="text-xs text-muted-foreground">
              これらの単語は再生成された歌詞に含まれません
            </p>
          </div>

          {/* 再生成の理由 */}
          <div className="space-y-2">
            <Label htmlFor="regenerate-reason">再生成の理由（オプション）</Label>
            <Textarea
              id="regenerate-reason"
              placeholder="例: もっと力強い表現にしたい"
              value={regenerateReason}
              onChange={(e) => setRegenerateReason(e.target.value)}
              rows={3}
              disabled={isGenerating}
            />
          </div>

          {/* 他のセクションを保持 */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="preserve-sections"
              checked={preserveOtherSections}
              onCheckedChange={(checked) => setPreserveOtherSections(checked as boolean)}
              disabled={isGenerating}
            />
            <Label
              htmlFor="preserve-sections"
              className="text-sm font-normal cursor-pointer"
            >
              他のセクションをそのまま保持
            </Label>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isGenerating}
          >
            キャンセル
          </Button>
          <Button onClick={handleSubmit} disabled={isGenerating}>
            {isGenerating ? '再生成中...' : '再生成'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
